import { useConfig } from "@/config.ts";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { PaymentResult, type PaymentParams, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import {
    failureToMessage,
    linkSendData,
    persistentDataRequest,
    pollForSuccess,
} from "@/modules/billing/payment/paymentUtils.ts";
import { t_StartingPayment, t_WaitingForPaymentConfirmation } from "@/modules/billing/progressLabels.tsx";
import { parseRazorpayPaymentResult } from "@/modules/billing/razorpay/parseRazorpayPersistentData.ts";
import { razorpayLoader, usePreloadRazorpay } from "@/modules/billing/razorpay/razorpayLoader.ts";
import { errorMessageAsString } from "@/modules/error/errorToString.ts";
import { SimpleLocalizedError } from "@/modules/l10n/getTransText.ts";
import { makePromise } from "@/utils/promise/makePromise.ts";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import { Trans } from "@lingui/react/macro";
import type { MethodConfig, ProcessorConfig, RazorpaySendDataResponse } from "@warrenio/api-spec/spec.oats.gen";
import { MINUTES } from "@warrenio/utils/timeUnits";
import { useImperativeHandle } from "react";

export type RazorpayLinkProcessorConfig = ProcessorConfig & { type: "link"; keyword: "razorpay" };

class RazorpayFailedError extends SimpleLocalizedError {
    name = "RazorpayFailedError";
    constructor(cause: unknown) {
        // Razorpay returns strange error object, parse message from it
        const failureMessage =
            typeof cause === "object" &&
            cause != null &&
            "error" in cause &&
            typeof cause.error === "object" &&
            cause.error != null &&
            "description" in cause.error &&
            typeof cause.error.description === "string"
                ? cause.error.description
                : errorMessageAsString(cause);
        super(failureToMessage({ failure_message: failureMessage }));
    }
}

export default function RazorpayPaymentForm({
    actionsRef,
    method: { key },
    config: { key_id },
}: {
    config: RazorpayLinkProcessorConfig;
    method: MethodConfig;
} & PaymentProps) {
    //#region Hooks

    const apiClient = useApiClient();
    const signal = useUnmountSignal();
    const { siteName } = useConfig();

    usePreloadRazorpay();

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return false;
        },

        async makePayment(params: PaymentParams) {
            const { progress, popup, account } = params;

            progress(t_StartingPayment);
            const { data } = await linkSendData<RazorpaySendDataResponse>(apiClient, params, "razorpay", {
                paymentMethod: key,
            });

            const { amount, title: description, order_id, persistent_data_ref } = data;

            type RazorpayEvent = "success" | "dismissed";
            const paymentPromise = makePromise<RazorpayEvent>();
            // XXX: Since we are not sure that `ondismiss` is only called on failure, we use a separate promise to track dismissal (so both promises could be resolved at the same time)
            const dismissedPromise = makePromise<RazorpayEvent>();

            const razorClass = await razorpayLoader();
            const rzp = new razorClass({
                key: key_id,
                name: siteName,
                amount,
                description,
                order_id,
                retry: {
                    enabled: false,
                },
                prefill: {
                    name: account.account.customer_name ?? "",
                    email: account.account.email,
                    contact: account.account.customer_phone ?? "",
                },
                handler: () => {
                    console.info("Razorpay payment successful");
                    paymentPromise.resolve("success");
                },
                modal: {
                    ondismiss: () => {
                        console.info("Razorpay dialog dismissed");
                        dismissedPromise.resolve("dismissed");
                    },
                },
            });
            rzp.on("payment.failed", (e) => {
                paymentPromise.reject(new RazorpayFailedError(e));
            });
            rzp.open();

            const timeout = setTimeout(() => {
                paymentPromise.reject(
                    new SimpleLocalizedError(<Trans>Timed out waiting for a response from Razorpay</Trans>),
                );
            }, 10 * MINUTES);

            try {
                // wait here, Razorpay callback should proceed the code flow
                const status = await Promise.race([paymentPromise.promise, dismissedPromise.promise]);
                // Check if the "dismiss" event happened _first_ (assume the payment was cancelled in that case)
                if (status === "dismissed") {
                    return PaymentResult.CANCELLED;
                }
            } finally {
                clearTimeout(timeout);
            }

            progress(t_WaitingForPaymentConfirmation);
            return await pollForSuccess({
                signal,
                popup,
                check: async () =>
                    parseRazorpayPaymentResult(
                        await persistentDataRequest(apiClient, account.id, persistent_data_ref, signal),
                    ) != null,
            });
        },
    }));
    //#endregion

    return null;
}
